// components/Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Navbar.css';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMobileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleHashClick = (e, targetHash) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
    if (location.pathname === '/') {
      const el = document.getElementById(targetHash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/#${targetHash}`);
    }
  };

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleLanguage = () => {
    const nextLang = i18n.language.startsWith('tr') ? 'en' : 'tr';
    i18n.changeLanguage(nextLang);
  };

  const currentLangCode = i18n.language.startsWith('tr') ? 'TR' : 'EN';
  const targetLangCode = i18n.language.startsWith('tr') ? 'EN' : 'TR';

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <div className="logo" onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
          <img src={'/conveks_final_transparent_sq.png?v=2'} alt="Conveks Logo" className='logo-img' />
        </div>

        <div className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <span></span>
          <span></span>
          <span></span>
        </div>

        <ul className={`nav-links ${mobileMenuOpen ? 'active' : ''}`}>
          <li>
            <Link to="/" onClick={handleLinkClick}>
              {t('navbar.home')}
            </Link>
          </li>
          
          {/* Solutions Dropdown */}
          <li className="dropdown-item" ref={dropdownRef}>
            <span 
              className={`dropdown-trigger ${mobileDropdownOpen ? 'open' : ''}`}
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
            >
              {t('navbar.solutions')}{' '}
              <svg className="arrow-icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <ul className={`dropdown-menu ${mobileDropdownOpen ? 'show' : ''}`}>
              <li>
                <Link to="/solutions/field-service-optimization" onClick={handleLinkClick}>
                  {t('solutionsDropdown.fieldService')}
                </Link>
              </li>
              <li>
                <Link to="/solutions/operational-forecasting" onClick={handleLinkClick}>
                  {t('solutionsDropdown.forecasting')}
                </Link>
              </li>
              <li>
                <Link to="/solutions/ops-analytics-anomaly-detection" onClick={handleLinkClick}>
                  {t('solutionsDropdown.analytics')}
                </Link>
              </li>
              <li>
                <Link to="/solutions/knowledge-intelligence" onClick={handleLinkClick}>
                  {t('solutionsDropdown.knowledge')}
                </Link>
              </li>
            </ul>
          </li>

          <li>
            <Link to="/about" onClick={handleLinkClick}>
              {t('navbar.about')}
            </Link>
          </li>

          <li>
            <Link to="/resources" onClick={handleLinkClick}>
              {t('navbar.resources')}
            </Link>
          </li>

          <li>
            <a href="#contact" onClick={(e) => handleHashClick(e, 'contact')}>
              {t('navbar.contact')}
            </a>
          </li>

          <li className="lang-toggle-li">
            <button 
              className="lang-toggle-btn" 
              onClick={toggleLanguage}
              aria-label="Switch Language"
            >
              <span className="lang-code current">{currentLangCode}</span>
              <span className="lang-divider">|</span>
              <span className="lang-code target">{targetLangCode}</span>
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;